import { Menu } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { type ReactNode, useState } from "react";
import { Link, NavLink, Outlet, useMatches } from "react-router-dom";

import { BrandMark, BrandWordmark } from "@/client/components/brand-logo";
import { ThemeToggle } from "@/client/components/theme-toggle";
import { Button } from "@/client/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/client/components/ui/sheet";
import { UserMenu } from "@/client/components/user-menu";
import { cn } from "@/client/lib/utils";

/** Small uppercase mono tag that names the current surface (Customer / Staff). */
function AudienceTag({ label }: { label: string }) {
  return (
    <span className="text-muted-foreground font-mono text-[10px] tracking-[0.14em] uppercase">
      {label}
    </span>
  );
}

export interface NavItem {
  label: string;
  to: string;
  icon: LucideIcon;
  /** Match the path exactly (use for index/home links). */
  end?: boolean;
}

function NavList({ nav, onNavigate }: { nav: NavItem[]; onNavigate?: () => void }) {
  return (
    <nav className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto p-3">
      {nav.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
              isActive
                ? "bg-accent text-accent-foreground"
                : "text-muted-foreground hover:bg-accent/50 hover:text-foreground",
            )
          }
        >
          <item.icon className="size-4" />
          {item.label}
        </NavLink>
      ))}
    </nav>
  );
}

/**
 * Bottom-of-rail account panel: the signed-in user and the light/dark toggle.
 * Living here (rather than a top bar) frees the full viewport height for content
 * on desktop, and it renders in both the desktop rail and the mobile nav drawer.
 */
function SidebarFooter() {
  return (
    <div className="flex shrink-0 items-center gap-1 border-t p-2">
      <div className="min-w-0 flex-1">
        <UserMenu />
      </div>
      <ThemeToggle />
    </div>
  );
}

/**
 * Shared authenticated layout: brand + left nav rail on desktop (with an account
 * footer pinned to the bottom), a slide-out drawer with the same nav on mobile,
 * and the routed content area. The top bar is mobile-only. Both the customer and
 * staff shells are thin wrappers that pass their own nav config.
 */
export function AppShell({
  audienceLabel,
  orgName,
  nav,
  homeTo,
  topSlot,
}: {
  audienceLabel: string;
  /** Tenant/org name shown in the brand block to personalize the surface. */
  orgName?: string | null;
  nav: NavItem[];
  /** Where the brand/logo links to (each audience's default landing page). */
  homeTo: string;
  /** Rendered above the routed content (e.g. the maintenance banner). */
  topSlot?: ReactNode;
}) {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // A route opts into using the full content width via `handle.fullWidth`;
  // everything else keeps the comfortable reading-width cap.
  const fullWidth = useMatches().some(
    (match) => (match.handle as { fullWidth?: boolean } | undefined)?.fullWidth,
  );

  return (
    <div className="flex min-h-svh">
      <aside className="bg-card sticky top-0 hidden h-svh w-60 shrink-0 flex-col border-r md:flex">
        <Link
          to={homeTo}
          className="hover:bg-accent/50 flex shrink-0 flex-col gap-1.5 border-b px-5 py-4 transition-colors"
        >
          <BrandWordmark className="w-[132px]" />
          {orgName ? (
            <span className="text-foreground truncate text-sm font-semibold" title={orgName}>
              {orgName}
            </span>
          ) : null}
          <AudienceTag label={audienceLabel} />
        </Link>
        <NavList nav={nav} />
        <SidebarFooter />
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Mobile-only top bar: brand + menu trigger. On desktop the rail (with
            its account footer) owns all of this, so content rises to the top. */}
        <header className="flex h-14 items-center gap-2 border-b px-4 md:hidden">
          <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Open menu">
                <Menu />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-64 p-0">
              <SheetHeader className="flex-col items-start gap-1.5 border-b py-4">
                <SheetTitle asChild>
                  <Link to={homeTo} onClick={() => setMobileNavOpen(false)}>
                    <BrandWordmark className="w-[124px]" />
                  </Link>
                </SheetTitle>
                {orgName ? (
                  <span className="text-foreground truncate text-sm font-semibold" title={orgName}>
                    {orgName}
                  </span>
                ) : null}
                <AudienceTag label={audienceLabel} />
              </SheetHeader>
              <NavList nav={nav} onNavigate={() => setMobileNavOpen(false)} />
              <SidebarFooter />
            </SheetContent>
          </Sheet>
          <Link to={homeTo} aria-label="Mantle home">
            <BrandMark className="size-7" />
          </Link>
        </header>
        <main className="flex-1 p-4 md:p-6">
          {/* A single content column owns the width so banners and every page
              share the same edges - pages no longer set their own width. It's
              left-aligned (no mx-auto) so content sits next to the nav instead
              of centered, avoiding a large gap on wide screens. */}
          <div className={cn("w-full", fullWidth ? undefined : "max-w-5xl")}>
            {topSlot}
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
